/**
 * RAISE DEVIATION CAPA COMPONENT
 * 
 * PURPOSE:
 * Manages welding deviation tracking and CAPA (Corrective and Preventive Action) workflow.
 * Provides a comprehensive dashboard for deviation management with status tracking.
 * 
 * KEY FEATURES:
 * 1. DEVIATION TRACKING GRID
 *    - Displays all welding deviations with comprehensive details
 *    - Columns: Status, Deviation Number, Slip Number, Project, Plant, Joint, Welder, etc.
 *    - Status-based color coding for visual identification
 *    - Clickable deviation numbers for detailed view/edit
 * 
 * 2. STATUS WORKFLOW MANAGEMENT
 *    - Pending for CAPA Analysis (Orange) → Initial state
 *    - Pending for Closure (Blue) → CAPA completed, awaiting closure
 *    - Closed (Gray) → Deviation resolved and closed
 *    - Rejected (Red) → Deviation rejected
 * 
 * 3. CAPA INTEGRATION
 *    - Links deviations to CAPA records via deviation number
 *    - Allows creation/editing of CAPA actions
 *    - Tracks corrective and preventive actions
 * 
 * 4. ATTACHMENT MANAGEMENT
 *    - Image file attachments for deviation evidence
 *    - Download functionality for deviation images
 *    - File storage in organized folder structure
 * 
 * 5. AGEING CALCULATION
 *    - Calculates days since deviation creation
 *    - Helps track resolution time and identify overdue items
 * 
 * 6. ROLE-BASED ACCESS
 *    - Different permissions based on user roles
 *    - Supervisor/QC can raise deviations
 *    - Management can approve/close deviations
 * 
 * ROLE PERMISSIONS (Based on Role Master):
 * 
 * RAISE DEVIATION:
 * - QCE (Quality Control Engineer): Can raise deviations during welding inspection
 * - Supervisor: Can raise deviations for process non-conformance
 * - NDE (Non-Destructive Examination): Can raise deviations during testing
 * - admin: Can raise deviations with full access
 * 
 * CAPA CREATION/EDITING:
 * - QCE: Primary role for CAPA analysis and corrective action planning
 * - MS (Management Staff): Can create/edit CAPA for critical deviations
 * - admin: Can override and modify any CAPA
 * 
 * CLOSURE/REJECTION:
 * - MS (Management Staff): Primary authority for deviation closure/rejection
 * - admin: Can close/reject any deviation with full system access
 * 
 * VIEW ONLY:
 * - WE (Welding Engineer): Can view deviations and CAPA for analysis
 * 
 * VALIDATION REQUIREMENTS:
 * MANDATORY: MS Code, Observer Code, Time, Category, Severity (1-5), Deviation Text
 * OPTIONAL: Slip Number (auto-fills fields), Project/Plant/Joint Numbers, Welder Code, Image
 * AUTO-GENERATED: Deviation Number, Date, Status, Aging
 * RULES: Role-based dropdowns, welder validation, file upload (JPEG/PNG, max 1MB)
 * 
 * DATA SOURCES:
 * - getAllDeviaitons(): Gets all deviation records with status and details
 * - getCapaDataByDevNumber(): Gets CAPA data linked to specific deviation
 * - downloadAttachment(): Downloads deviation image attachments
 * - getUsersL1(): Gets user list for assignment/approval workflows
 * 
 * EXPORT CAPABILITIES:
 * - Excel export with all deviation details
 * - Comprehensive column mapping for analysis
 * - Formatted output for management reporting
 * 
 * RAISING CONDITIONS: Current/Voltage range, Travel speed, Heat input, Joint prep, Welder qualification, Environment
 * FUNCTIONS: Report generation, CAPA management, Email notifications
 * API: raise-deviation, get deviations, update-status, capa linking, reports, notifications
 * 
 * WORKFLOW:
 * 1. RAISE: QCE/Supervisor/NDE → Validate → Auto-generate number → "Pending CAPA" → Email MS
 * 2. CAPA: QCE → Root cause + Actions → Link to deviation → "Pending Closure" → Email MS  
 * 3. CLOSE: MS → Review effectiveness → Close/Reject → Final status → Email all
 * 4. TRACK: Aging calculation, Excel reports, attachments, audit trail
 */
import React, { useState, useRef, forwardRef, useImperativeHandle, useMemo, useEffect } from "react";
import SuccessAlertModal from "../Modals/successAlertModal";
import { useNavigate, Link } from "react-router-dom";
import GolbalSearch from "../utility/common/helpers/GolbalSearch";
import { handleExport1 } from "../utility/data/export/GlobalExport";
import { Row, Col, Container, Card } from "react-bootstrap";
import AddRaiseDeviationCapa from "./AddRaiseDeviationCapa";
import GlobalColumnDefinations, { getHeader_FieldName } from "../utility/grid/definitions/AgGridDefination";
import { showError } from "../utility/ui/notifications/Toaster";
import Export_blue from "../../icons/Export_blue.svg";
import ExportIcon from "../../icons/ExportIcon.svg";
import { getAllDeviaitons, getCapaDataByDevNumber, downloadAttachment } from "../../Services/deviationcapa-service";
import Pagination from "../utility/grid/Pagination";
import { getCurrentDateTime } from "../../functions/welding-functions";
import { getUsersL1 } from "../../Services/user-service";
import GridMaster from "../utility/grid/GridMaster";


// Inline renderer components
const DeviationStatusRenderer = (props) => {
  const devNum = props.value || props.data?.devstatus;
  let status_color = "";
  let bk_color = "";
  let font__Size = "";
  let Margin_Size = "";
  let Rstatus = devNum;
  switch (Rstatus) {
    case "Pending for CAPA Analysis":
      status_color = "var(--godrej-orange)";
      bk_color = "var(--godrej-orange-bg)";
      font__Size = "12px";
      Margin_Size = "160px";
      break;
    case "Pending for Closure":
      status_color = "var(--godrej-blue)";
      bk_color = "var(--godrej-blue-bg)";
      font__Size = "12px";
      Margin_Size = "125px";
      break;
    case "Closed":
      status_color = "var(--txt-color)";
      bk_color = "var(--bg-alt-2)";
      font__Size = "12px";
      Margin_Size = "55px";
      break;
    case "Rejected":
      status_color = "var(--godrej-red)";
      bk_color = "var(--godrej-red-bg)";
      font__Size = "12px";
      Margin_Size = "55px";
      break;
    default:
      status_color = "var(--txt-color)";
      bk_color = "var(--bg-alt-2)";
      font__Size = "12px";
      Margin_Size = "120px";
      break;
  }

  return (
    <div
      style={{
        backgroundColor: bk_color,
        borderRadius: "4px",
        width: Margin_Size,
        height: "30px"
      }}
      className="d-flex align-items-center"
    >
      <span style={{ color: status_color, borderRadius: "14px", fontSize: font__Size }}>
        &nbsp;{Rstatus}&nbsp;
      </span>
    </div>
  );
};
const HyperLinkRenderer = (props) => (
  <Link onClick={() => props.onDeviationClick(props.data)} className="add-new-link">
    {parseFloat(props.data.deviationNumber)}
  </Link>
);

const ImageHyperLinkRenderer = (props) => (
  <Link onClick={() => props.onImageClick(props.data)} className="add-new-link">
    {props.data.deviationImageFile}
  </Link>
);

const DeviationNumberRenderer = ({ data, handleDevClick }) => (
  <Link onClick={handleDevClick} style={{ paddingLeft: "0px" }} className="add-new-link">
    {parseFloat(data)}
  </Link>
);

const DeviationImageRenderer = ({ data, handleImageClick }) => (
  <Link onClick={handleImageClick} style={{ paddingLeft: "0px" }} className="add-new-link">
    {data}
  </Link>
);

const RaiseDeviationCapa = forwardRef((props, ref) => {
  const [rowData, setRowData] = useState({});
  const [capaData, setCapaData] = useState({});
  const [filteredResult, setFilteredResult] = useState([]);
  const [columnDefs, setcolumnDefs] = useState([]);
  const [deviations, setDeviations] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [action, setAction] = useState("");
  const [reload, setReload] = useState(false);
  const [excelData1, setExcelData] = useState([]);
  const [showMore, setShowMore] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const msg = "";
  const history = useNavigate();
  const pageSize = 3;
  const [currentPage, setCurrentPage] = useState(1);
  const handleCloseSuccess = () => setShowSuccess(false);
  const [usersL1Result, setUsersL1Result] = useState([]);
  let salCode = sessionStorage.getItem("SalCode");
  const gridRef = useRef();
  const handleDeviation = async (row) => {
    let result = await getCapaDataByDevNumber(row.deviationNumber);
    setAction("Editcapa");
    setRowData(row);
    if (result?.length > 0) {
      setCapaData(result[0]);
    }
    else {
      setCapaData({});
    }
    setShowForm(true);
  };

  const handleImageDeviation = (row) => {
    downloadAttachment({ foldername: "Deviations", subfoldername: row.deviationNumber, filename: row.deviationImageFile })
      .catch((err) => {
        showError(`Error in fetching file.`);
      });
  };

  const tmpcolumnDefs = [
    {
      headerName: "Status",
      field: "devstatus",
      type: "Text",
      minWidth: 160,
      resizable: false,
      isCheckbox: false,
      cellRenderer: "RaiseCellRenderer",
      cellClass: 'status-column',
    },
    {
      headerName: "Deviation Number",
      field: "deviationNumber",
      width: 120,
      minWidth: 120,
      type: "Number",
      resizable: false,
      cellRenderer: HyperLinkRenderer,
      cellRendererParams: { onDeviationClick: handleDeviation }
    },
    {
      headerName: "Slip Number",
      field: "slipNumber",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "",
    },
    {
      headerName: "Project Number",
      field: "projectNumber",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "",
    },
    {
      headerName: "Plant Number",
      field: "plantNumber",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "",
    },
    {
      headerName: "Joint Number",
      field: "jointNumber",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "",
    },
    {
      headerName: "Welder Code",
      field: "welderCode",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "",
    }, {
      headerName: "Supervisor Code",
      tooltipField: "Supervisor Code",
      field: "supervisorCode",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "",
    }, {
      headerName: "Observer Code",
      tooltipField: "Observer Code",
      field: "observerCode",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "",
    }, {
      headerName: "Observation Date",
      tooltipField: "Observation Date",
      field: "observationDate",
      type: "Date",
      isCheckbox: false,
      cellRenderer: "dateCellRenderer",
      filterParams: "filterDate",
    }, {
      headerName: "Observation Time",
      tooltipField: "Observation Time",
      field: "observationTime",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "",
    }, {
      headerName: "Deviation Category Desc",
      field: "deviationCategoryDesc",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "",
    },
    {
      headerName: "Deviation Severity Level",
      field: "deviationSeverityLevel",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "",
    }, {
      headerName: "Updated By",
      field: "updatedByName",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "AvatarCellRenderer",
    },
    {
      headerName: "Updated On",
      field: "updatedOn",
      type: "Text",
      filter: 'agDateColumnFilter',
      isCheckbox: false,
      cellRenderer: "",
    }, {
      headerName: "Deviation Text",
      field: "deviationText",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "",
    },
    {
      headerName: "Image",
      field: "deviationImageFile",
      isCheckbox: false,
      cellRenderer: ImageHyperLinkRenderer,
      cellRendererParams: { onImageClick: handleImageDeviation }
    },
    {
      headerName: "Ageing (Days)",
      field: "ageing(days)",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "",
    },
  ];

  const headerName = (field) => {
    let name = getHeader_FieldName(field, tmpcolumnDefs, "getHeader");
    return name;
  };

  const fieldName = (header) => {
    let name = getHeader_FieldName(header, tmpcolumnDefs, "getField");
    return name;
  };

  function sortByPropertyInAscending(property) {
    return function (a, b) {
      if (a[property] > b[property]) return 1;
      else if (a[property] < b[property]) return -1;
      return 0;
    };
  }

  const fetchRaise = async () => {
    const result = await getAllDeviaitons();
    let todayDate = getCurrentDateTime("-");
    for (let i = 0; i < result?.length; i++) {
      let date1 = new Date(result[i].observationDate);
      let date2 = new Date(todayDate);
      let time_difference = date2.getTime() - date1.getTime();
      result[i]["ageing(days)"] = Math.round(time_difference / (1000 * 60 * 60 * 24));
      result[i].observationDate = result[i].observationDate.substring(0, 10);
      result[i].updatedOn = result[i].updatedOn.substring(0, 10);
    };
    result?.sort(sortByPropertyInAscending("deviationNumber"))
    setDeviations(result);
    setFilteredResult(result);
  };

  const fetchColumnDefns = async () => {
    const result = GlobalColumnDefinations(tmpcolumnDefs);
    result[0].cellRenderer = DeviationStatusRenderer;
    result[1].cellRenderer = HyperLinkRenderer;
    result[1].cellRendererParams = { onDeviationClick: handleDeviation };
    result[16].cellRenderer = ImageHyperLinkRenderer;
    result[16].cellRendererParams = { onImageClick: handleImageDeviation };
    setcolumnDefs(result);
  };

  useEffect(() => {
    fetchRaise();
    fetchColumnDefns();
  }, [reload]);

  const fetchUsersL1Code = async () => {
    let result = await getUsersL1({ emailId: salCode });
    console.log("result", result)
    setUsersL1Result(result);
  };

  useEffect(() => {
    fetchUsersL1Code();
  }, [action]);

  useImperativeHandle(ref, () => ({
    handleAdd: async () => {
      setAction("Add");
      if (usersL1Result?.length !== 0 && usersL1Result?.[0].empEmailId !== "") {
        setShowForm(true);
        setReload(true);
      }
      else {
        showError(`User's L+1 EmailId not Maintained.`);
      }
    },
  }));

  const resetForm = () => {
    document.filterForm.reset();
    setFilteredResult(deviations);
  };

  const modalOnClick = () => {
    history.push({
      pathname: "/",
      data: "setDeviations",
    });
  };

  const handleGlobalSearch = (text) => {
    gridRef.current.setSearch(text);

  };

  const handleGlobalExport = () => {
    let filename = "RaiseDeviationDetails";
    setExcelData(filteredResult);
    handleExport1(excelData1, filename, tmpcolumnDefs);
  };

  const handleCardClick = (deviationNumber) => {
    if (showMore[deviationNumber]) {
      setShowMore((prev) => ({ ...prev, [deviationNumber]: false }));
    }
    else {
      setShowMore((prev) => ({ ...prev, [deviationNumber]: true }));
    }
  }

  const filteredResulttemp = useMemo(() => {
    let firstPageIndex = 0;
    if (currentPage > 1) {
      firstPageIndex = (currentPage - 1) * pageSize;
    }
    if (firstPageIndex >= filteredResult?.length) {
      firstPageIndex = 0;
      const totalPageCount = Math.ceil(filteredResult?.length / pageSize);
      if (totalPageCount > 0) {
        setCurrentPage(totalPageCount);
      } else {
        setCurrentPage(1);
      }
    }
    let lastPageIndex = parseInt(firstPageIndex) + parseInt(pageSize);
    return filteredResult
      ? filteredResult.slice(firstPageIndex, lastPageIndex)
      : [];
  }, [currentPage, filteredResult, pageSize]);



  return (
    <Container fluid className="main-container">
      <SuccessAlertModal
        clickFunction={modalOnClick}
        msg={msg}
        show={showSuccess}
        handleClose={handleCloseSuccess}
      />
      <Row className="RowHeight">
        <Col md={3} sm={12} xs={12} className="ListTitle">
          Raise Deviation / CAPA Analysis list
        </Col>
        <Col>
          <div className="global-search-container">
            <GolbalSearch
              filterFunc={handleGlobalSearch}
              searchplaceholdertext={"Search for Status, Deviation Number,Slip Number..."}
            />
            <img
              className="allexport "
              src={ExportIcon}
              onMouseOver={(e) => (e.currentTarget.src = Export_blue)}
              onMouseOut={(e) => (e.currentTarget.src = ExportIcon)}
              alt="ExportIcon"
              onClick={handleGlobalExport}
            />
          </div>
        </Col>
      </Row>

      <div className="ag-theme-material d-none d-md-block d-lg-block">
        <GridMaster
          rowDetails={deviations}
          colDetails={columnDefs}
          fieldNames={fieldName}
          headerNames={headerName}
          getDataEvent={(getFilteredData) =>
            setFilteredResult(getFilteredData)
          }

          setExcelData={setExcelData}
          ref={gridRef}
        />
      </div>
      <div className="scrollable-container">
        <div className="market-wrapper d-md-none d-lg-none table-containerDeviation">
          {
            filteredResulttemp?.length > 0 && <> {
              filteredResulttemp.map((selection) =>
                <Card key={`deviation-${selection.deviationNumber}`} className="cardContainer">
                  <Card.Body className="cardbodycontainer">
                    {columnDefs.map((header, col_index) => {
                      const renderCellData = () => {
                        if (header.headerName === "Status") {
                          return <DeviationStatusRenderer value={selection[header.field]} data={selection} />;
                        }
                        if (header.headerName === "Deviation Number") {
                          return <DeviationNumberRenderer data={selection[header.field]} handleDevClick={() => handleDeviation(selection)} />;
                        }
                        if (header.headerName === "Image") {
                          return <DeviationImageRenderer data={selection[header.field]} handleImageClick={() => handleImageDeviation(selection)} />;
                        }
                        return selection[header.field];
                      };

                      return (header.field && header.field !== "" && (col_index < 5 || (col_index >= 5 && showMore[selection.deviationNumber])) &&
                        <Row key={`row-${header.field}`} className="responsive-row">
                          <Col className="colHeader">{header.headerName}</Col>
                          <Col className="colData">{renderCellData()}</Col>
                        </Row>
                      )
                    })
                    }
                  </Card.Body>
                  <Card.Footer className="responsive-card-footer">
                    <Row className="responsive-row">
                      <Col className="customerArrowResponsive">
                        <span id={selection.deviationNumber}
                          onClick={() => { handleCardClick(selection.deviationNumber) }}
                          className="show-more-link">{showMore[selection.deviationNumber] ? "Show less" : "Show More"}</span>
                      </Col>
                    </Row>
                  </Card.Footer>
                </Card>
              )}
              {filteredResulttemp?.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalCount={filteredResult ? filteredResult?.length : 0}
                  data={filteredResult}
                  pageSize={pageSize}
                  onPageChange={(page) => setCurrentPage(page)}
                />
              )}
            </>
          }
        </div>
      </div>
      {
        showForm && (
          <AddRaiseDeviationCapa
            show={showForm}
            usersL1Result={usersL1Result}
            action={action}
            rowdata={rowData}
            onHide={() => setShowForm(false)}
            resetForm={resetForm}
            setReload={setReload}
            reload={reload}
            caparowdata={capaData}
            alldeviationsdata={deviations}
            backdrop="static"
          />
        )
      }

    </Container >
  );
});

export default RaiseDeviationCapa;